import React, { createContext, useContext, useState, useEffect } from 'react';

const SettingsContext = createContext();

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};

export const SettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState(() => {
    const savedSettings = localStorage.getItem('kb_user_settings');
    return savedSettings ? JSON.parse(savedSettings) : {
      baseFontSize: 14,
      headingScale: 1.25,
      primaryColor: '#3b82f6',
      darkMode: false,
      compactMode: false,
    };
  });

  useEffect(() => {
    localStorage.setItem('kb_user_settings', JSON.stringify(settings));
    applySettings(settings);
  }, [settings]);

  const applySettings = (s) => {
    const root = document.documentElement;

    // Apply Font Sizes
    root.style.setProperty('--base-font-size', `${s.baseFontSize}px`);
    root.style.setProperty('--heading-scale', s.headingScale);

    // Apply Primary Color
    root.style.setProperty('--primary-color', s.primaryColor);

    // Apply Dark Mode Class
    if (s.darkMode) {
      document.documentElement.classList.add('dark');
      document.body.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
      document.body.classList.remove('dark');
    }

    // Apply Compact Mode Class
    if (s.compactMode) {
      document.body.classList.add('compact-mode');
    } else {
      document.body.classList.remove('compact-mode');
    }
  };

  const updateSettings = (newSettings) => {
    setSettings(prev => ({ ...prev, ...newSettings }));
  };

  const resetSettings = () => {
    setSettings({
      baseFontSize: 14,
      headingScale: 1.25,
      primaryColor: '#3b82f6',
      darkMode: false,
      compactMode: false,
    });
  };

  return (
    <SettingsContext.Provider value={{ settings, updateSettings, resetSettings }}>
      {children}
    </SettingsContext.Provider>
  );
};
